import styled from 'styled-components';
import ReviewMetaTag from '@/components/mo/Review/ReviewMetaTag';
import AddIcon from '@/assets/pc/add.svg?react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { fetchReviewList } from '@/api/review';
import { useInView } from 'react-intersection-observer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import EyesIcon from '@/assets/mo/eyes.svg?react';

interface ReviewCardProps {
  selectedFilters: {
    보험종류: string[];
    사고유형: string[];
  };
  sort: 'recent' | 'view';
}

const ReviewCard = ({ selectedFilters, sort }: ReviewCardProps) => {
  const { ref, inView } = useInView();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const query = searchParams.get('query') || '';
  const searchType = searchParams.get('searchType') || '전체';

  const queryKey = [
    'reviewList',
    sort,
    {
      보험종류: [...selectedFilters.보험종류].sort(),
      사고유형: [...selectedFilters.사고유형].sort(),
      query,
      searchType,
    },
  ];

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } =
    useInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam = 0 }) => {
        const insuranceTags = selectedFilters.보험종류.includes('전체')
          ? []
          : selectedFilters.보험종류.filter((t) => t !== '전체');

        const issueTags = selectedFilters.사고유형.includes('전체')
          ? []
          : selectedFilters.사고유형.filter((t) => t !== '전체');

        const tags = [...insuranceTags, ...issueTags];

        let keyword: string | undefined;
        if (searchType === '전체') {
          keyword = query;
        } else if (searchType === '제목') {
          keyword = `title:${query}`;
        } else if (searchType === '내용') {
          keyword = `content:${query}`;
        }

        return fetchReviewList({
          page: pageParam,
          size: 10,
          sort: sort === 'recent' ? ['incidentDate,desc'] : ['viewCount,desc'],
          tags: tags.length > 0 ? tags : undefined,
          keyword: keyword?.trim() || undefined,
        });
      },
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.number + 1;
        return nextPage < lastPage.totalPages ? nextPage : undefined;
      },
      initialPageParam: 0,
    });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) return <div>로딩 중...</div>;
  if (isError) return <div>에러 발생</div>;

  const allReviews = data?.pages.flatMap((page) => page.content) ?? [];

  const matchesFilter = (tags: string[]) => {
    const { 보험종류, 사고유형 } = selectedFilters;
    const insuranceMatch = 보험종류.includes('전체') || 보험종류.some((t) => tags.includes(t));
    const accidentMatch = 사고유형.includes('전체') || 사고유형.some((t) => tags.includes(t));
    return insuranceMatch && accidentMatch;
  };

  const filteredReviews = allReviews.filter((review) =>
    matchesFilter(review.tags.map((t) => t.tag)),
  );

  if (filteredReviews.length === 0)
    return (
      <EmptyMessageWrapper>
        <EmptyText>조건에 맞는 리뷰가 없습니다.</EmptyText>
      </EmptyMessageWrapper>
    );

  return (
    <Wrapper>
      <CardRow>
        {filteredReviews.map((review) => (
          <CardContainer key={review.id} onClick={() => navigate(`/review/${review.id}`)}>
            <CardInner>
              <TopRow>
                <TagRow>
                  {review.tags.map((tag) => (
                    <Tag key={tag.id}>{tag.tag}</Tag>
                  ))}
                </TagRow>
                <ViewCountWrapper>
                  <EyesIcon />
                  <ViewCountText>{review.viewCount.toLocaleString()}</ViewCountText>
                </ViewCountWrapper>
              </TopRow>
              <Title>{review.title}</Title>
              <Divider />
              <MetaAndContent>
                <MetaSection>
                  <ReviewMetaTag
                    label={review.issues[0]?.isPayoutApproved ? '지급' : '부지급'}
                    totalFee={review.issues[0]?.totalFee}
                  />
                </MetaSection>
                <ContentSection>
                  <ContentRow>
                    <ContentText>
                      {review.content.length > 150
                        ? `${review.content.slice(0, 150)}...`
                        : review.content}
                    </ContentText>
                  </ContentRow>
                  <MoreRow>
                    <MoreRight>
                      {' '}
                      {review.incidentDate
                        ? new Date(review.incidentDate).toLocaleDateString('ko-KR')
                        : ''}
                    </MoreRight>
                    <MoreLeft>
                      <MoreText>더보기</MoreText>
                      <AddIcon />
                    </MoreLeft>
                  </MoreRow>
                </ContentSection>
              </MetaAndContent>
            </CardInner>
          </CardContainer>
        ))}
      </CardRow>
      <div ref={ref} style={{ height: 1 }} />
    </Wrapper>
  );
};

export default ReviewCard;

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  max-width: 1240px;
  padding: 0 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  box-sizing: border-box;
`;

const CardRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: start; // ✅ 카드 수가 1개일 때 가운데 정렬
  gap: 24px;
  width: 100%;
`;

const MoreText = styled.div`
  color: var(--gray-ui_gray01, #191919);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.28px;
  cursor: pointer;
`;

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background: var(--Primary-white, #fff);
  cursor: pointer;

  width: 100%;
  flex: 0 0 380px;
  max-width: 380px;
  margin-bottom: 24px;

  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0px 0px 64px 0px rgba(0, 0, 0, 0.06);

    ${MoreText} {
      color: #0b46dd !important;
    }

    svg {
      path {
        fill: #0b46dd;
      }
    }
  }

  @media (max-width: 1024px) {
    flex: 0 0 calc(50% - 12px);
    max-width: calc(50% - 12px);
  }

  @media (max-width: 768px) {
    flex: 0 0 calc(50% - 12px);
    max-width: calc(50% - 12px);
  }
`;

const CardInner = styled.div`
  width: 100%; // ✅ 부모 width 따름
  height: 431px;
  padding: 31px 26px;
  flex-direction: column;
  align-items: flex-start;
  gap: 18px;
  border-radius: 4px;
  border: 1px solid var(--table-line-table_line03, #d1d1d1);
  background: var(--Primary-white, #fff);
  display: flex;

  @media (max-width: 767px) {
    height: auto; // ✅ 내용에 따라 늘어나도록
  }
`;

const TagRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Tag = styled.div`
  color: var(--gray-ui_gray05, #999);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.28px;
`;

const Title = styled.div`
  align-self: stretch;
  color: var(--gray-ui_gray01, #191919);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 18px;
  font-weight: 500;
  line-height: 120%;
  letter-spacing: -0.36px;
  text-align: start;
`;

const Divider = styled.div`
  width: 84.676px;
  height: 2.674px;
  flex-shrink: 0;
  background: var(--gray-ui_gray01, #191919);
`;

const MetaAndContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  align-self: stretch;
`;

const MetaSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 9px;
  flex: 1 0 0;
  align-self: stretch;
`;

const ContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1 0 0;
  align-self: stretch;
`;

const ContentText = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 7; /* ✅ 2줄까지 출력 */
  overflow: hidden;
  text-overflow: ellipsis;

  color: var(--gray-ui_gray03, #3a3a3a);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-weight: 400;
  line-height: 188%;
  letter-spacing: -0.28px;
  text-align: start;
`;

const MoreRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  cursor: pointer;
`;

const EmptyMessageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const EmptyText = styled.div`
  color: var(--gray-ui_gray04, #666);
  font-size: 18px;
  font-weight: 400;
  font-family: 'Spoqa Han Sans Neo';
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;

const ViewCountWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ViewCountText = styled.div`
  color: var(--gray-ui_gray05, #999);
  text-align: center;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%; /* 16.8px */
  letter-spacing: -0.28px;
`;

const MoreLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MoreRight = styled.div`
  color: var(--gray-ui_gray05, #999);
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%;
  letter-spacing: -0.28px;
`;
